import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight, CheckCircle2, Package, Settings, Shield, Layers, Award, Target, FileCheck, RefreshCw } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const MyntraServices = () => {
  const listingServices = [
    'Product title and attribute optimization',
    'Category and sub-category mapping',
    'Product description and feature updates',
    'Image selection and catalog compliance checks',
    'Ensuring listings meet Myntra guidelines'
  ];

  const catalogOptimizationServices = [
    'Product description optimization',
    'Attribute and specification updates',
    'Category corrections',
    'Improving product information clarity',
    'Updating missing or incorrect catalog details'
  ];

  const brandApprovalServices = [
    'Guiding through Myntra brand approval requirements',
    'Submitting necessary brand documentation',
    'Ensuring compliance with Myntra brand policies',
    'Assisting in resolving approval issues'
  ];

  const variationServices = [
    'Setting up correct variation relationships',
    'Managing size and color variations',
    'Ensuring proper parent-child listing structure',
    'Updating incorrect or missing variation details'
  ];

  const whyChooseUs = [
    { icon: Award, title: 'Expertise in Myntra Catalog Management', description: 'Deep understanding of Myntra platform requirements and best practices.' },
    { icon: FileCheck, title: 'Accurate Product Listing & Content Optimization', description: 'Precise catalog entries that meet all platform guidelines.' },
    { icon: Shield, title: 'Assistance with Brand Approval Processes', description: 'Complete support for brand onboarding and documentation.' },
    { icon: Layers, title: 'Proper Variation Setup & Catalog Structuring', description: 'Organized product variations for better customer experience.' },
    { icon: RefreshCw, title: 'Ongoing Catalog Maintenance & Updates', description: 'Continuous monitoring and updates to keep your catalog optimized.' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Myntra Product Listing & Catalog Services | Obiquiste Solutions</title>
        <meta name="description" content="Professional Myntra product listing and catalog management. Fashion-focused optimization for better visibility. Launch your brand on Myntra today." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/myntra-listing-catalog-optimization" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Myntra Product Listing & Catalog Optimization</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 64, 129, 0.03) 0%, rgba(233, 30, 99, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-pink-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-rose-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                Myntra Product Listing & <span className="bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">Catalog Optimization Services</span>
              </h1>
              <p className="text-lg leading-relaxed text-slate-700 mb-6 max-w-3xl mx-auto">
                Selling successfully on Myntra requires accurate catalog creation, optimized product content, and proper brand approvals. A well-structured product listing helps improve product visibility, ensures compliance with Myntra guidelines, and enhances the overall shopping experience for customers.
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8 max-w-3xl mx-auto">
                At Obiquiste Solutions, we help brands create and optimize Myntra product listings to ensure that products are presented correctly, categorized properly, and fully optimized for the platform. Our team focuses on maintaining catalog accuracy, improving product presentation, and ensuring smooth onboarding through Myntra's listing processes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact?service=myntra">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Get Free Catalog Audit
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=myntra">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    Talk to Myntra Expert
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Myntra Product Listing Creation */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-pink-100 rounded-xl mb-6">
                <Package className="w-7 h-7 text-pink-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Myntra Product Listing Creation
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                Creating a product listing on Myntra requires following platform guidelines and ensuring all required information is entered correctly.
              </p>
              <p className="text-lg text-slate-700">
                Our team helps brands create accurate product listings with optimized content and proper category selection. Well-structured listings help products appear correctly on the platform and improve customer understanding of the product.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Listing Creation Services Include:</h3>
                <div className="space-y-4">
                  {listingServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Catalog Optimization */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Catalog Optimization Services Include:</h3>
                <div className="space-y-4">
                  {catalogOptimizationServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-blue-800 text-sm">
                    <strong>Result:</strong> This helps improve product discoverability and enhances the customer shopping experience.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-100 rounded-xl mb-6">
                <Settings className="w-7 h-7 text-purple-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Catalog Optimization
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                Catalog optimization plays an important role in ensuring product information is complete, consistent, and easy for customers to understand.
              </p>
              <p className="text-lg text-slate-700">
                At Obiquiste Solutions, we optimize catalog content to improve product presentation and maintain accuracy across the platform.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Myntra Brand Approval Assistance */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-xl mb-6">
                <Shield className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Myntra Brand Approval Assistance
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                Before listing products under a brand name on Myntra, brands must go through the platform's brand approval process.
              </p>
              <p className="text-lg text-slate-700">
                Our team assists businesses in completing the necessary steps required to obtain brand approval and ensure smooth listing of branded products. This process helps ensure that products can be listed under the correct brand identity on the platform.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Brand Approval Support Includes:</h3>
                <div className="space-y-4">
                  {brandApprovalServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Variation Management */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Variation Management Services Include:</h3>
                <div className="space-y-4">
                  {variationServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-green-800 text-sm">
                    <strong>Benefit:</strong> Proper variation management allows customers to easily choose product options and improves the overall catalog structure.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-indigo-100 rounded-xl mb-6">
                <Layers className="w-7 h-7 text-indigo-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Product Variation Management
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                Correctly managing product variations is essential for creating a better shopping experience and maintaining a clean catalog structure.
              </p>
              <p className="text-lg text-slate-700">
                We ensure that product variations such as size, color, or style are configured properly within the Myntra catalog.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Obiquiste Solutions */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Why Choose Obiquiste Solutions for Myntra Listing Services
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Our goal is to ensure that your Myntra listings are accurate, well-organized, and optimized to provide a better customer experience.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20"
              >
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-slate-300 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-12 border border-pink-200">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Ready to Optimize Your Myntra Catalog?
            </h2>
            <p className="text-lg md:text-xl text-slate-700 mb-8">
              Let our experts help you create accurate listings, optimize your catalog, and improve your product visibility on Myntra.
            </p>
            <Link to="/contact?service=myntra">
              <Button className="bg-primary text-white hover:bg-primary-600 px-10 py-7 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl">
                Get Free Catalog Audit
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyntraServices;
